import { ApplicationOffer, ApplicationOfferBundleData } from "../../application/application.model";
import { ProductCode } from "../../product/product.model";
import { isLargeLoanProgramOffer } from "./large-loan-program.model";

export interface LineOfCreditOfferGroups<T> {
  standard: T[];
  largeLoanProgram: T[];
}

// The best (largest) offer per product card on the offer summary page, in display order:
// Line of Credit, large loan program Line of Credit, Case Financing, Term.
// Equal limits go to the longest term, then the earlier bundle.
export function getBestOfferBundles(offerBundles: ApplicationOfferBundleData[]): ApplicationOfferBundleData[] {
  let bestLoc: ApplicationOfferBundleData;
  let bestLargeLoan: ApplicationOfferBundleData;
  let bestLaw: ApplicationOfferBundleData;
  let bestTerm: ApplicationOfferBundleData;

  offerBundles?.forEach((bundle) => {
    switch (bundle.offers?.[0]?.productCode) {
      case ProductCode.LOC:
        if (isLargeLoanProgramOffer(bundle))
          bestLargeLoan = getLargerOfferBundle(bestLargeLoan, bundle);
        else
          bestLoc = getLargerOfferBundle(bestLoc, bundle);
        break;
      case ProductCode.Law:
        bestLaw = getLargerOfferBundle(bestLaw, bundle);
        break;
      case ProductCode.Term:
        bestTerm = getLargerOfferBundle(bestTerm, bundle);
        break;
    }
  });

  return [bestLoc, bestLargeLoan, bestLaw, bestTerm].filter((bundle) => !!bundle);
}

export function splitLineOfCreditOffers<T extends { largeLoanProgram: boolean }>(
  locOfferBundles: T[]
): LineOfCreditOfferGroups<T> {
  return {
    standard: locOfferBundles?.filter((x) => !x.largeLoanProgram) ?? [],
    largeLoanProgram: locOfferBundles?.filter((x) => x.largeLoanProgram) ?? [],
  };
}

// Offer tab order: express offers first, then the highest limit first. Array.sort is stable, so
// offers with the same limit keep their incoming (longest-term-first) order.
export function compareOfferTabs(
  a: { express?: boolean; amount: number },
  b: { express?: boolean; amount: number }
): number {
  return Number(!!b.express) - Number(!!a.express) || b.amount - a.amount;
}

// The offer a product tab opens: the first of that product's offer tabs.
export function getFirstOfferTab<T extends { express?: boolean; amount: number }>(
  offerBundles: T[]
): T {
  return [...(offerBundles ?? [])].sort(compareOfferTabs)[0];
}

// The highest value of one offer field across every bundle, not just the cards. Bundles without an
// offer come back from the API and are skipped.
export function getMaxOfferValue(
  offerBundles: ApplicationOfferBundleData[],
  value: (offer: ApplicationOffer) => number
): number {
  const values = offerBundles?.filter((x) => x.offers?.[0]).map((x) => value(x.offers[0])) ?? [];
  return values.length ? Math.max(...values) : undefined;
}

// The offer a card's SEE ALL OFFERS opens: the one its product tab opens. It can differ from the
// card's own offer, since an express offer opens first even with a lower limit.
export function getFirstOfferTabBundle(
  offerBundles: ApplicationOfferBundleData[],
  card: ApplicationOfferBundleData
): ApplicationOfferBundleData {
  const productCode = card.offers[0].productCode;
  const largeLoanProgram = isLargeLoanProgramOffer(card);
  const product = (offerBundles ?? []).filter(
    (x) => x.offers?.[0]?.productCode === productCode && isLargeLoanProgramOffer(x) === largeLoanProgram
  );
  return getFirstOfferTab(
    [...product]
      .sort(compareOfferBundles)
      .map((bundle) => ({ bundle, express: bundle.expressCheckout, amount: bundle.offers[0].amount }))
  )?.bundle;
}

// The order offer tabs arrive in before compareOfferTabs: express first, then the longest term.
// A bundle without an offer sorts as a 0-month term, so it can't stop the others being reordered.
export function compareOfferBundles(a: ApplicationOfferBundleData, b: ApplicationOfferBundleData): number {
  return Number(!!b.expressCheckout) - Number(!!a.expressCheckout)
    || (b.offers?.[0]?.repaymentTerm ?? 0) - (a.offers?.[0]?.repaymentTerm ?? 0);
}

function getLargerOfferBundle(
  current: ApplicationOfferBundleData,
  candidate: ApplicationOfferBundleData
): ApplicationOfferBundleData {
  if (!current) return candidate;
  if (current.offers[0].amount === candidate.offers[0].amount)
    return current.offers[0].repaymentTerm >= candidate.offers[0].repaymentTerm ? current : candidate;
  return current.offers[0].amount > candidate.offers[0].amount ? current : candidate;
}
